import { ReactElement, useContext, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"

import { Button, Loading } from "@wfp/ui"

import { DataUploadContext } from "../../providers/DataUploadContext"
import useCommon from "../../hooks/useCommon"
import { useChangeReportStatusMutation } from "../../services/marketprices/reports"

import { modaValidationSteps } from "../../constants/markets"
import { DataUploadSteps } from "../../interfaces/markets"
import { ReportStatuses } from "../../constants/prices"

import ProgressStepper from "../../components/Shared/ProgressStepper/ProgressStepper"

const ModaValidation = (): ReactElement => {
  const navigate = useNavigate()
  const { showSnackBar } = useCommon()

  const { filters, isLoading, disabledNext } = useContext(DataUploadContext)
  const [activeStep, setActiveStep] = useState<number>(DataUploadSteps.VALIDATION)

  const [changeReportStatus, { isLoading: isLoadingStatus }] = useChangeReportStatusMutation()

  const handleBack = (): void => {
    if (activeStep > DataUploadSteps.VALIDATION) {
      setActiveStep(prev => prev - 1)
      return
    }
    navigate(-1)
  }

  const handleNext = async (): Promise<void> => {
    if (activeStep >= DataUploadSteps.FINALIZE) {
      try {
        await changeReportStatus({ id: filters.reportId!, status: ReportStatuses.Pending }).unwrap()
        showSnackBar("Report submitted successfully")
        navigate("/market-prices/reports")
      } catch (error) {
        const err = error as FetchBaseQueryError
        showSnackBar(
          (err?.data as { message: string })?.message ?? "An error occurred while submitting report",
          "error",
        )
      }
      return
    }

    setActiveStep(prev => prev + 1)
  }

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 125px)" }}>
      <div className="w-full border-b-2 border-solid border-gray-200 pb-1 pt-2 bg-white z-30 flex-shrink-0">
        <ProgressStepper steps={modaValidationSteps} currentStep={activeStep} />
      </div>

      <div className="flex-1 overflow-y-auto pt-8 px-6">
        {isLoading ? (
          <div className="min-h-[400px] w-full flex justify-center items-center">
            <Loading small withOverlay={false} />
          </div>
        ) : (
          modaValidationSteps.find(item => item.id === activeStep)?.content
        )}
      </div>

      <div className="flex justify-between w-full border-t-2 border-solid border-gray-200 pt-4 pb-4 bg-white px-6 flex-shrink-0">
        <Button kind="secondary" onClick={handleBack}>
          {activeStep > DataUploadSteps.VALIDATION ? "Back" : "Cancel"}
        </Button>

        <Button onClick={handleNext} disabled={disabledNext || isLoading || isLoadingStatus}>
          {modaValidationSteps?.find(item => item.id === activeStep)?.nextAction || "Next"}
        </Button>
      </div>
    </div>
  )
}

export default ModaValidation
